import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonEmpty, EuiCheckableCard, EuiFieldText, EuiFlexGrid, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiPage, EuiSpacer, EuiText } from '@elastic/eui'

import { useGetMasterCampaignQuery } from 'api/rtkQueryApi/platform/masterCampaignsApi'

import { Account, PermissionType } from '../../../api/interfaces'
import { useCreatCampaignSimpleMutation, useGetRetargetingCampaignsQuery } from '../../../api/rtkQueryApi/platform/campaignsApi'
import { updateAccountState } from '../../../app/appSlice'
import { RootState } from '../../../app/rootReducer'
import { ClientPicker } from '../../../components/account/ClientPicker'
import { AgencyV2ClientPageLayout } from '../../../components/layouts/AgencyV2ClientPageLayout'
import { useOrganizationPermissions } from '../../../providers/OrganizationPermissionsContext'
import history from '../../../services/HistoryService'
import { BuilderCampaignType } from '../../../workflows/CampaignCreateWorkflow'
import { useWhiteLabel } from '../../../whiteLabel/WhiteLabelContext'
import { setCampaign as setGlobalCampaign } from '../../campaigns/campaign/campaignSlice'

type SoloTargetingSelection = 'standard' | 'retargeting' | 'audienceUpload'

type SoloCampaignStep = 'details' | 'targetingType'

interface ISimpleCampaign {
  campaignName: string
  campaignType: BuilderCampaignType
  targetingType: SoloTargetingSelection
}

export const SimpleCampaignPage: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const masterCampaignId = urlParams.get('masterCampaignId')

  const [createCampaign, createCampaignRequest] = useCreatCampaignSimpleMutation()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const whiteLabel = useWhiteLabel()
  const retargetingCampaignsQuery = useGetRetargetingCampaignsQuery(
    { accountId: currentAccount?.id ?? '', isAgency: whiteLabel?.isAgencies() },
    { skip: !currentAccount }
  )
  const hasRetargetingCampaigns = (retargetingCampaignsQuery.data?.length ?? 0) > 0
  const hasPixelInstalled = currentAccount?.hasVisits ?? false
  const canRetarget = hasRetargetingCampaigns || hasPixelInstalled

  const { hasPermission } = useOrganizationPermissions()

  const [step, setStep] = React.useState<SoloCampaignStep>('details')

  const { data: masterCampaign, isFetching: isFetchingMasterCampaign } = useGetMasterCampaignQuery(
    {
      masterCampaignId: masterCampaignId ?? ''
    },
    {
      skip: !masterCampaignId
    }
  )
  const [campaign, setCampaign] = React.useState<ISimpleCampaign>(() => {
    const urlCampaignType = urlParams.get('campaignType')
    let campaignType = BuilderCampaignType.TV
    switch (urlCampaignType) {
      case 'TV':
        campaignType = BuilderCampaignType.TV
        break
      case 'Audio':
        campaignType = BuilderCampaignType.Audio
        break
      case 'Billboard':
        campaignType = BuilderCampaignType.Billboard
        break
      case 'Internet':
      case 'Display':
        campaignType = BuilderCampaignType.Display
        break
      case 'Native':
        campaignType = BuilderCampaignType.Native
        break
    }
    return {
      campaignName: '',
      campaignType: campaignType,
      targetingType: 'standard'
    }
  })

  const dispatch = useDispatch()

  const onClientChange = (client: Account) => {
    dispatch(updateAccountState(client))
  }

  const campaignTypeOption: { id: BuilderCampaignType; label: string }[] = [
    {
      id: BuilderCampaignType.TV,
      label: 'Connected TV (CTV)'
    },
    {
      id: BuilderCampaignType.Billboard,
      label: 'Digital Billboard'
    },
    {
      id: BuilderCampaignType.Audio,
      label: 'Streaming Audio'
    },
    {
      id: BuilderCampaignType.Display,
      label: 'Internet Display'
    },
    {
      id: BuilderCampaignType.Native,
      label: 'Internet Native'
    }
  ]

  const isBillboard = campaign.campaignType === BuilderCampaignType.Billboard

  const supportsUploadAudienceType = campaign.campaignType === BuilderCampaignType.Display || campaign.campaignType === BuilderCampaignType.Native || campaign.campaignType === BuilderCampaignType.TV || campaign.campaignType === BuilderCampaignType.Audio
  const retargetingAvailable = canRetarget && !isBillboard
  const audienceUploadAvailable = hasPermission(PermissionType.UploadAudience) && supportsUploadAudienceType
  const hasTargetingChoices = retargetingAvailable || audienceUploadAvailable

  const targetingTypeOptions: { id: SoloTargetingSelection; label: string; description: string }[] = [
    {
      id: 'standard',
      label: 'Standard Targeting',
      description: 'Target precisely by demo, geo, behavior, context, and persona'
    },
    ...(retargetingAvailable
      ? [
          {
            id: 'retargeting' as SoloTargetingSelection,
            label: 'Retargeting Campaign',
            description: 'Re-engage audiences collected by your pixel or existing campaigns.'
          }
        ]
      : []),
    ...(audienceUploadAvailable
      ? [
          {
            id: 'audienceUpload' as SoloTargetingSelection,
            label: 'Uploaded Audience List',
            description: 'Target your audience'
          }
        ]
      : [])
  ]

  const changeCampaignType = (campaignType: BuilderCampaignType) => {
    setStep('details')
    setCampaign(c => ({
      ...c,
      campaignType,
      targetingType: 'standard'
    }))
  }

  const createSoloCampaign = async () => {
    if (!currentAccount) {
      return
    }
    const newCampaign = await createCampaign({
      accountId: currentAccount.id,
      campaignName: campaign.campaignName,
      campaignType: campaign.campaignType,
      masterCampaignId: masterCampaignId ?? undefined,
      isRetargeting: campaign.targetingType === 'retargeting',
      isAudienceUpload: campaign.targetingType === 'audienceUpload'
    }).unwrap()

    dispatch(
      setGlobalCampaign({
        ...newCampaign,
        masterCampaignId: masterCampaignId ?? ''
      })
    )

    history.push(`/campaigns/edit/${newCampaign.id}/details`)
  }

  const onCreateClick = () => {
    if (hasTargetingChoices) {
      setStep('targetingType')
      return
    }
    createSoloCampaign()
  }

  const detailsPanelTitle = masterCampaignId ? `Add Campaign to ${masterCampaign?.name}` : 'Create Solo Campaign'
  const isTargetingTypeStep = step === 'targetingType'

  return (
    <EuiPage className='euiPage--platform'>
      <AgencyV2ClientPageLayout isLoading={isFetchingMasterCampaign} helmetTitle={masterCampaignId ? 'Add Campaign to Master Campaign' : 'Create Solo Campaign'} panelTitle={isTargetingTypeStep ? 'Choose Targeting Type' : detailsPanelTitle}>
        {isTargetingTypeStep ? (
          <>
            <EuiFormRow label={'Targeting Type'}>
              <EuiFlexGrid columns={1} responsive={false} gutterSize={'s'} style={{ paddingLeft: '50px', marginTop: '4px' }}>
                {targetingTypeOptions.map(option => (
                  <EuiFlexItem key={option.id}>
                    <EuiCheckableCard
                      id={`targetingType-${option.id}`}
                      value={option.id}
                      checked={campaign.targetingType === option.id}
                      checkableType={'radio'}
                      label={
                        <EuiText size={'m'}>
                          <strong>{option.label}</strong>
                          <EuiText size={'xs'} color={'subdued'}>
                            {option.description}
                          </EuiText>
                        </EuiText>
                      }
                      onChange={() => setCampaign(c => ({ ...c, targetingType: option.id }))}
                    />
                  </EuiFlexItem>
                ))}
              </EuiFlexGrid>
            </EuiFormRow>
            <EuiSpacer size='m' />
            <EuiFormRow>
              <EuiFlexGroup gutterSize={'s'} responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiButtonEmpty onClick={() => setStep('details')}>Back</EuiButtonEmpty>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={createSoloCampaign} isLoading={createCampaignRequest.isLoading} disabled={!currentAccount}>
                    Create Campaign
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFormRow>
          </>
        ) : (
          <>
            <EuiFormRow label='Client'>
              <ClientPicker selectedClient={currentAccount ?? undefined} onClientChange={onClientChange} />
            </EuiFormRow>
            <EuiFormRow label={'Campaign Name'}>
              <EuiFieldText value={campaign.campaignName} onChange={e => setCampaign({ ...campaign, campaignName: e.target.value })} />
            </EuiFormRow>
            <EuiFormRow label={'Campaign Type'}>
              <EuiFlexGrid columns={1} responsive={false} gutterSize={'s'} style={{ paddingLeft: '50px', marginTop: '4px' }}>
                {campaignTypeOption.map(option => (
                  <EuiFlexItem key={option.id}>
                    <EuiCheckableCard
                      id={option.id}
                      value={option.id}
                      checked={campaign.campaignType === option.id}
                      checkableType={'radio'}
                      label={<EuiText size={'m'}>{option.label}</EuiText>}
                      onChange={() => changeCampaignType(option.id)}
                    />
                  </EuiFlexItem>
                ))}
              </EuiFlexGrid>
            </EuiFormRow>
            <EuiSpacer size='m' />
            <EuiFormRow>
              <EuiButton fill onClick={onCreateClick} isLoading={createCampaignRequest.isLoading} disabled={campaign.campaignName.length < 1 || !currentAccount}>
                Create
              </EuiButton>
            </EuiFormRow>
          </>
        )}
      </AgencyV2ClientPageLayout>
    </EuiPage>
  )
}
